import React, { useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiBarChart2,
  FiCamera,
  FiChevronsLeft,
  FiChevronsRight,
  FiCpu,
  FiDatabase,
  FiGrid,
  FiImage,
  FiKey,
  FiRefreshCw,
  FiSettings,
  FiSmartphone,
  FiTool,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';

type SaktiSidebarMenuKey =
  | 'home'
  | 'rkh'
  | 'bcc'
  | 'bcc-images'
  | 'spb'
  | 'bkm'
  | 'access-code'
  | 'configuration'
  | 'push-odoo'
  | 'face-registry'
  | 'attendance-audit'
  | 'app-config'
  | 'remote-inspection';

interface SaktiSidebarMenuItem {
  key: SaktiSidebarMenuKey;
  label: string;
  icon: React.ElementType;
  path?: string;
}

const MENU_ITEMS: SaktiSidebarMenuItem[] = [
  { key: 'home', label: 'Home', icon: FiGrid, path: '/admin/sakti' },
  { key: 'rkh', label: 'RKH', icon: FiDatabase, path: '/admin/sakti/rkh' },
  { key: 'access-code', label: 'Kode Akses', icon: FiKey, path: '/admin/sakti/access-code' },
  { key: 'attendance-audit', label: 'Audit Absensi', icon: FiCamera, path: '/admin/sakti/attendance-audit' },
  { key: 'bcc', label: 'BCC', icon: FiBarChart2, path: '/admin/sakti/bcc' },
  { key: 'spb', label: 'SPB', icon: FiBarChart2, path: '/admin/sakti/spb' },
  { key: 'bkm', label: 'BKM', icon: FiTool, path: '/admin/sakti/bkm' },
  { key: 'configuration', label: 'Configuration', icon: FiSettings, path: '/admin/sakti/configuration' },
  { key: 'push-odoo', label: 'Re-sync Odoo', icon: FiRefreshCw, path: '/admin/sakti/push-odoo' },
  { key: 'bcc-images', label: 'Download Gambar BCC', icon: FiImage, path: '/admin/sakti/bcc-images' },
  { key: 'face-registry', label: 'Face Registry', icon: FiCamera, path: '/admin/sakti/face-registry' },
  { key: 'app-config', label: 'App Config', icon: FiSmartphone, path: '/admin/sakti/app-config' },
  { key: 'remote-inspection', label: 'Remote DB', icon: FiCpu, path: '/admin/sakti/remote-inspection' },
];

interface SaktiSidebarProps {
  activeMenu?: SaktiSidebarMenuKey;
}

const SaktiSidebar: React.FC<SaktiSidebarProps> = ({ activeMenu = 'home' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const sidebarWidth = isCollapsed ? '72px' : '260px';

  return (
    <VStack
      w={{ base: 'full', xl: sidebarWidth }}
      h="100vh"
      bg="white"
      borderRightWidth="1px"
      borderRightColor="gray.200"
      spacing={0}
      align="stretch"
      px={isCollapsed ? 2 : 5}
      py={6}
      position={{ base: 'relative', xl: 'sticky' }}
      top={0}
      overflow="hidden"
      transition="width 0.25s ease, padding 0.25s ease"
      flexShrink={0}
    >
      {/* Logo / Branding */}
      <Box mb={isCollapsed ? 4 : 8} flexShrink={0}>
        {isCollapsed ? (
          <VStack align="center" spacing={1} pt={1}>
            <HStack spacing={1} justify="center">
              <Box w="8px" h="8px" borderRadius="full" bg="customGreen.500" />
              <Box w="8px" h="8px" borderRadius="full" bg="customGreen.500" />
            </HStack>
            <Text fontWeight="bold" fontSize="xs" color="customGreen.600" letterSpacing="wider">
              SK
            </Text>
          </VStack>
        ) : (
          <VStack align="start" spacing={0}>
            <HStack spacing={1} mb={1}>
              <Box w="10px" h="10px" borderRadius="full" bg="customGreen.500" />
              <Box w="10px" h="10px" borderRadius="full" bg="customGreen.500" />
            </HStack>
            <Text fontWeight="bold" fontSize="2xl" lineHeight="shorter">
              SAKTI
            </Text>
            <Text fontWeight="bold" fontSize="2xl" lineHeight="shorter">
              E-Harvesting
            </Text>
            <Text fontSize="xs" color="gray.500" mt={1}>
              SAKTI MANAGEMENT
            </Text>
          </VStack>
        )}
      </Box>

      {/* Menu Items (Scrollable) */}
      <VStack
        align="stretch"
        spacing={2}
        flex={1}
        overflowY="auto"
        mb={4}
        pr={isCollapsed ? 0 : 1}
        css={
          isCollapsed
            ? {
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                'msOverflowStyle': 'none',
                'scrollbarWidth': 'none',
              }
            : {
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#E2E8F0',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#CBD5E1',
                },
              }
        }
      >
        {MENU_ITEMS.map((menu) => {
          const isActive = activeMenu === menu.key;
          const menuItem = (
            <HStack
              key={menu.key}
              p={3}
              borderRadius="md"
              bg={isActive ? 'gray.100' : 'transparent'}
              color={isActive ? 'customGreen.500' : 'gray.600'}
              borderLeftWidth={isActive && !isCollapsed ? '3px' : '0px'}
              borderLeftColor="customGreen.500"
              transition="all 0.2s"
              cursor={menu.path ? 'pointer' : 'default'}
              opacity={menu.path ? 1 : 0.85}
              justify={isCollapsed ? 'center' : 'flex-start'}
              onClick={() => {
                if (menu.path) {
                  navigate(menu.path);
                }
              }}
            >
              <Box as={menu.icon} flexShrink={0} />
              {!isCollapsed && (
                <Text fontWeight={isActive ? 'semibold' : 'medium'}>{menu.label}</Text>
              )}
            </HStack>
          );

          return isCollapsed ? (
            <Tooltip key={menu.key} label={menu.label} placement="right" hasArrow>
              {menuItem}
            </Tooltip>
          ) : (
            menuItem
          );
        })}
      </VStack>

      {/* Separator Line */}
      <Divider borderColor="gray.200" mb={4} flexShrink={0} />

      {/* Bottom section */}
      <VStack align="stretch" spacing={3} mt="auto" flexShrink={0}>
        {/* User info — hidden when collapsed */}
        {!isCollapsed && (
          <Box bg="gray.50" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
            <HStack align="start" spacing={3}>
              <Avatar size="sm" name={user?.email || 'Admin'} />
              <VStack align="start" spacing={0}>
                <Text fontWeight="semibold" fontSize="sm" noOfLines={1} maxW="150px">
                  {user?.email || 'Admin User'}
                </Text>
                <Badge colorScheme="orange" fontSize="0.65rem">
                  ADMIN ACCESS
                </Badge>
              </VStack>
            </HStack>
          </Box>
        )}

        {/* Avatar only when collapsed */}
        {isCollapsed && (
          <Tooltip label={user?.email || 'Admin User'} placement="right" hasArrow>
            <Box display="flex" justifyContent="center">
              <Avatar size="sm" name={user?.email || 'Admin'} />
            </Box>
          </Tooltip>
        )}

        {/* Back button */}
        {isCollapsed ? (
          <Tooltip label="Back to admin" placement="right" hasArrow>
            <IconButton
              aria-label="Back to admin"
              icon={<FiArrowLeft />}
              variant="ghost"
              color="gray.600"
              onClick={() => navigate('/admin')}
            />
          </Tooltip>
        ) : (
          <Button
            variant="ghost"
            justifyContent="flex-start"
            leftIcon={<FiArrowLeft />}
            onClick={() => navigate('/admin')}
            color="gray.600"
          >
            Back to admin
          </Button>
        )}

        {/* Toggle button */}
        <Box display="flex" justifyContent={isCollapsed ? 'center' : 'flex-end'}>
          <Tooltip
            label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            placement="right"
            hasArrow
          >
            <IconButton
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              icon={isCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
              size="sm"
              variant="outline"
              bg="white"
              borderColor="gray.300"
              borderWidth="1px"
              color="gray.500"
              _hover={{ color: 'customGreen.500', bg: 'gray.50', borderColor: 'customGreen.500' }}
              onClick={() => setIsCollapsed((prev) => !prev)}
            />
          </Tooltip>
        </Box>
      </VStack>
    </VStack>
  );
};

export default SaktiSidebar;
